import api from "../../config/axios";
import type { CleaningStaffTaskStatus } from "./cleaningStatus";

export {
  motivoLabelsPorEstado,
  type CleaningStaffTaskStatus,
} from "./cleaningStatus";
export type CleaningStaffTaskType = "checkout" | "intermedia" | "manual" | "verificacion";

export interface CleaningStaffTask {
  task_key: string;
  fecha_limpieza: string;
  departamento: string;
  tipo_limpieza: CleaningStaffTaskType;
  reserva_id?: number | null;
  cliente?: string;
  entrada?: string;
  salida?: string;
  zona?: string;
  responsable?: string;
  tipo_cama_reserva?: string | null;
  tipo_cama_preparar?: string | null;
  /** Señal operativa sin datos identificatorios: hoy entra alguien a este
   * departamento, así que hay que limpiar antes del ingreso. Es el único
   * indicador de esto que recibe el rol limpieza (no recibe la reserva
   * entrante). */
  hay_ingreso_hoy?: boolean;
  reserva_entrante_id?: number | null;
  cliente_entrante?: string | null;
  fecha_original?: string | null;
  fecha_programada?: string | null;
  motivo_reprogramacion?: string | null;
  advertencia_sin_personal?: string | null;
  anticipada_sin_personal?: boolean;
  estado: CleaningStaffTaskStatus;
  motivo_no_realizada?: string | null;
  nota?: string | null;
  shampoo_recargado?: boolean;
  acondicionador_recargado?: boolean;
  marcado_por?: {
    usuario_id?: number | null;
    nombre?: string | null;
    email?: string | null;
    rol?: string | null;
  } | null;
  marcado_at?: string | null;
  bloqueada_por_supervision?: boolean;
  /** false cuando la reserva se editó después de marcar y la tarea ya no está programada. */
  tarea_vigente?: boolean;
}

export interface CleaningStaffTasksResponse {
  fecha: string;
  tareas: CleaningStaffTask[];
  resumen: {
    total: number;
    pendientes: number;
    limpias: number;
    /** Compatibilidad: servicio viejo "parcial", hoy mostrado como solo ropa. */
    parciales: number;
    /** Cualquier tarea donde se cambió ropa, esté limpia completa o no. */
    con_ropa?: number;
    no_limpias: number;
  };
}

export interface CleaningCalendarRecordsResponse {
  desde: string;
  hasta: string;
  registros: CleaningStaffTask[];
}

export interface UpdateCleaningTaskStatusPayload {
  estado: CleaningStaffTaskStatus;
  motivo_no_realizada?: string | null;
  nota?: string | null;
  /** Ausentes = sin cambios: marcar "Limpio" de un toque no borra lo registrado. */
  shampoo_recargado?: boolean;
  acondicionador_recargado?: boolean;
}

export async function fetchCleaningStaffTasks(fecha: string): Promise<CleaningStaffTasksResponse> {
  const response = await api.get<CleaningStaffTasksResponse>("/limpieza/operativa/tareas", {
    params: { fecha },
  });
  return response.data;
}

export async function fetchCleaningCalendarRecords(
  desde: string,
  hasta: string
): Promise<CleaningCalendarRecordsResponse> {
  const response = await api.get<CleaningCalendarRecordsResponse>("/limpieza/operativa/registros", {
    params: { desde, hasta },
  });
  return response.data;
}

export async function updateCleaningStaffTaskStatus(
  taskKey: string,
  payload: UpdateCleaningTaskStatusPayload
): Promise<CleaningStaffTask> {
  const response = await api.put<{ tarea: CleaningStaffTask }>(
    `/limpieza/operativa/tareas/${encodeURIComponent(taskKey)}/estado`,
    payload
  );
  return response.data.tarea;
}

/** Fechas pactadas de la reserva que el personal ya marcó: no se pueden quitar. */
export async function fetchFechasPactadasRegistradas(reservaId: number): Promise<string[]> {
  const response = await api.get<{ fechas_pactadas_registradas: string[] }>(
    `/limpieza/operativa/registros/reserva/${reservaId}`
  );
  return response.data.fechas_pactadas_registradas;
}
